import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import PhoneIcon from '@mui/icons-material/Phone';
import React, { useState } from 'react';
import { FieldErrors, UseFormReturn, useFieldArray } from 'react-hook-form';

import {
  AiVoice,
  allCompanyCategories,
  allInvoicedServices,
  CallCenterDetailDTO,
  Company,
  companyCategoryDisplayName,
  ConstantArray,
  InvoicedServiceEnum,
  Paths,
  PathValue
} from '_lib/api/apiTypes.shim';
import { PrimaryCardHeader } from '_lib/components/CardHeader/CardHeader';
import { CheckboxFormItem } from '_lib/components/CheckboxFormItem/CheckboxFormItem';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { FlexCard } from '_lib/components/FlexCard/FlexCard';
import { IconButton } from '_lib/components/IconButton/IconButton';
import { List } from '_lib/components/List/List';
import { ListItem } from '_lib/components/ListItem/ListItem';
import { ListItemButton } from '_lib/components/ListItemButton/ListItemButton';
import { ListItemText } from '_lib/components/ListItemText/ListItemText';
import { MenuItem } from '_lib/components/MenuItem/MenuItem';
import { SelectFormItem } from '_lib/components/SelectFormItem/SelectFormItem';
import { Stack } from '_lib/components/Stack/Stack';
import { Tooltip } from '_lib/components/Tooltip/Tooltip';
import { Typography } from '_lib/components/Typography/Typography';
import { IconTypography } from '_lib/featureComponents/IconTypography/IconTypography';
import { useConfirmationDialog } from '_lib/hooks/ConfirmationDialogHook';
import { extractAllErrors, formatPhone } from '_lib/utils/helpers';

import { DnisEditCard } from './DnisEditCard';
import { VoiceEditCard } from './VoiceEditCard';

export const companyConfigSettingsFormValues = [
  'aiConfig.voices',
  'callCenter.dnisArray',
  'callCenter.phoneCallWebhookEnabled',
  'callCenter.reporting.shouldReport',
  'callCenter.reporting.companyCategory',
  'callCenter.reporting.invoicedService'
] satisfies ConstantArray<Paths<Company>>;

export type CompanyConfigSettingsFormValue = (typeof companyConfigSettingsFormValues)[number];

export type CompanyConfigSettingsFormValues = {
  [K in CompanyConfigSettingsFormValue]: PathValue<Company, K>;
};

/**
 * Form is only responsible for fields it controls, and we can check for the generic constraint in the
 * props in order for this to be reusable.
 */
type CompanyConfigSettingsFormProps<T extends CompanyConfigSettingsFormValues> = {
  companyConfigSettingsFormContext: T extends CompanyConfigSettingsFormValues ? UseFormReturn<T> : never;
  callCenter: CallCenterDetailDTO;
  aiVoices: AiVoice[];
  company?: Company;
  voicesAreLoading?: boolean;
  onSave?: () => Promise<void>;
};

export const CompanyConfigSettingsForm = <T extends CompanyConfigSettingsFormValues>(
  props: CompanyConfigSettingsFormProps<T>
) => {
  const { companyConfigSettingsFormContext, callCenter, company, aiVoices, voicesAreLoading, onSave } = props;
  const {
    control,
    watch,
    formState: { errors }
  } = companyConfigSettingsFormContext;

  const confirmationDialog = useConfirmationDialog();
  const isCompanyActive = !company || company.isActive;

  const voiceFields = (watch('aiConfig.voices') as CompanyConfigSettingsFormValues['aiConfig.voices']) || [];
  const dnisFields = (watch('callCenter.dnisArray') as CompanyConfigSettingsFormValues['callCenter.dnisArray']) || [];

  const {
    append: appendVoice,
    remove: removeVoice,
    update: updateVoice
  } = useFieldArray({
    control,
    name: 'aiConfig.voices'
  });

  const {
    append: appendDnis,
    remove: removeDnis,
    update: updateDnis
  } = useFieldArray({
    control,
    name: 'callCenter.dnisArray'
  });

  const [editingVoiceIndex, setEditingVoiceIndex] = useState<number | null>(null);
  const [editingDnisIndex, setEditingDnisIndex] = useState<number | null>(null);

  const getVoiceName = (voiceId: string) => {
    const voice = aiVoices.find((v) => v.id === voiceId);
    return voice
      ? `${voice.gender === 'male' ? '♂' : '♀'} ${voice.name} (${voice.providerVoice.provider})`
      : voicesAreLoading
        ? 'Loading...'
        : 'Voice Display Error';
  };

  const getVoiceDescription = (voiceId: string) => {
    const voice = aiVoices.find((v) => v.id === voiceId);
    return voice?.description
      ? voice.description
      : voicesAreLoading
        ? 'Loading...'
        : 'Voice Display Error - Unable to find voice';
  };

  const handleSaveVoice = async (voiceData: { primaryVoiceId: string; fallbackVoiceId: string }) => {
    if (editingVoiceIndex === -1) {
      appendVoice(voiceData);
    } else if (editingVoiceIndex !== null) {
      updateVoice(editingVoiceIndex, voiceData);
    }
    await onSave?.();
    setEditingVoiceIndex(null);
  };

  const handleDeleteVoice = async (index: number) => {
    const confirmRemove = await confirmationDialog.show({
      titleText: 'Delete Preferred Voice?',
      contentText: `This will remove "${voiceFields?.[index]?.primaryVoiceId ? getVoiceName(voiceFields?.[index]?.primaryVoiceId) : 'Unknown Voice'}" from the preferred voices.`
    });
    if (confirmRemove?.action === 'CANCEL') return;

    removeVoice(index);
    await onSave?.();
  };

  const handleSaveDnis = async (dnisData: {
    phoneNumber: string;
    greetingOverride: string | null;
    aiGreetingOverride: string | null;
  }) => {
    if (editingDnisIndex === -1) {
      appendDnis(dnisData);
    } else if (editingDnisIndex !== null) {
      updateDnis(editingDnisIndex, dnisData);
    }
    await onSave?.();
    setEditingDnisIndex(null);
  };

  const handleDeleteDnis = async (index: number, dnisHasError: boolean) => {
    if (dnisHasError) {
      removeDnis(index);
      await onSave?.();
      return;
    }

    const confirmRemove = await confirmationDialog.show({
      titleText: 'Delete DNIS Number?',
      contentText: `This will remove "${dnisFields?.[index]?.phoneNumber ? formatPhone(dnisFields?.[index]?.phoneNumber) : 'Unknown DNIS'}" from the DNIS list.`
    });
    if (confirmRemove?.action === 'CANCEL') return;

    removeDnis(index);
    await onSave?.();
  };

  return (
    <Stack useFlexGap spacing={2} sx={{ width: '100%' }}>
      <FlexCard>
        <PrimaryCardHeader
          title={<IconTypography variant='h3' startIcon={<AutoAwesomeIcon />} text='Preferred CPI Voices' />}
          action={
            editingVoiceIndex === null && (
              <Tooltip title='Add Voice'>
                <IconButton
                  color='primaryInverted'
                  onClick={() => setEditingVoiceIndex(-1)}
                  disabled={!isCompanyActive}
                  sx={{ marginRight: 0.5 }}>
                  <AddCircleOutlineIcon />
                </IconButton>
              </Tooltip>
            )
          }
        />
        <Stack useFlexGap sx={{ width: '100%' }} spacing={2}>
          {editingVoiceIndex === null && voiceFields.length === 0 && aiVoices?.[0] && (
            <Typography
              text={`No preferred voice selected? No Worries! We'll use our best performing voice: ${getVoiceName(aiVoices[0].id)}`}
              sx={{ padding: 2 }}
            />
          )}
          {editingVoiceIndex === null && voiceFields.length > 0 && (
            <List disablePadding>
              {voiceFields.map((voice, index) => (
                <ListItem
                  key={index}
                  divider
                  disablePadding
                  secondaryAction={
                    <IconButton edge='end' onClick={() => handleDeleteVoice(index)} disabled={!isCompanyActive}>
                      <DeleteIcon />
                    </IconButton>
                  }>
                  <Tooltip
                    title='Edit Voice'
                    followCursor
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: 'offset',
                            options: {
                              offset: [0, 10]
                            }
                          }
                        ]
                      }
                    }}>
                    <ListItemButton onClick={() => setEditingVoiceIndex(index)} disabled={!isCompanyActive}>
                      <ListItemText
                        primary={
                          <Stack useFlexGap spacing={0}>
                            <span>{getVoiceName(voice.primaryVoiceId)}</span>
                            <IconTypography
                              startIcon={<DescriptionIcon sx={{ fontSize: 14 }} />}
                              text={getVoiceDescription(voice.primaryVoiceId)}
                            />
                          </Stack>
                        }
                        secondary={getVoiceName(voice.fallbackVoiceId)}
                      />
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              ))}
            </List>
          )}
          {editingVoiceIndex !== null && (
            <VoiceEditCard
              initialPrimaryId={editingVoiceIndex !== -1 ? voiceFields[editingVoiceIndex]?.primaryVoiceId : null}
              initialFallbackId={editingVoiceIndex !== -1 ? voiceFields[editingVoiceIndex]?.fallbackVoiceId : null}
              aiVoices={aiVoices}
              isActive={isCompanyActive}
              onCancel={() => setEditingVoiceIndex(null)}
              onSave={handleSaveVoice}
            />
          )}
        </Stack>
      </FlexCard>
      <FlexCard>
        <PrimaryCardHeader
          title='DNIS (Phone Numbers)'
          action={
            editingDnisIndex === null && (
              <Tooltip title='Add DNIS'>
                <IconButton
                  color='primaryInverted'
                  onClick={() => setEditingDnisIndex(-1)}
                  disabled={!isCompanyActive}
                  sx={{ marginRight: 0.5 }}>
                  <AddCircleOutlineIcon />
                </IconButton>
              </Tooltip>
            )
          }
        />
        <Stack useFlexGap sx={{ width: '100%' }} spacing={2}>
          {editingDnisIndex === null && dnisFields.length === 0 && (
            <Typography
              text='No DNIS numbers added yet. Click "Add DNIS" to add your first number.'
              sx={{ padding: 2 }}
            />
          )}
          {editingDnisIndex === null && dnisFields.length > 0 && (
            <List disablePadding>
              {dnisFields.map((dnisItem, index) => {
                const formErrors = errors as FieldErrors<Company>;
                const dnisItemErrors = formErrors?.callCenter?.dnisArray?.[index];
                const mappedErrors = dnisItemErrors
                  ? [...(dnisItemErrors.message ? [dnisItemErrors.message] : []), ...extractAllErrors(dnisItemErrors)]
                  : [];
                return (
                  <ListItem
                    key={index}
                    divider
                    disablePadding
                    secondaryAction={
                      <IconButton
                        edge='end'
                        onClick={() => handleDeleteDnis(index, mappedErrors.length > 0)}
                        disabled={!isCompanyActive}>
                        <DeleteIcon />
                      </IconButton>
                    }>
                    <Tooltip
                      title='Edit DNIS'
                      followCursor
                      slotProps={{
                        popper: {
                          modifiers: [
                            {
                              name: 'offset',
                              options: {
                                offset: [0, 10]
                              }
                            }
                          ]
                        }
                      }}>
                      <ListItemButton onClick={() => setEditingDnisIndex(index)}>
                        <ListItemText
                          primary={
                            <IconTypography
                              startIcon={<PhoneIcon sx={{ fontSize: 14 }} />}
                              text={formatPhone(dnisItem.phoneNumber) || 'Phone number display error'}
                            />
                          }
                          secondary={
                            mappedErrors.length > 0 ? (
                              <Typography variant='caption' color='error' text={mappedErrors.join(', ')} />
                            ) : undefined
                          }
                        />
                      </ListItemButton>
                    </Tooltip>
                  </ListItem>
                );
              })}
            </List>
          )}
          {editingDnisIndex !== null && (
            <DnisEditCard
              initialPhoneNumber={editingDnisIndex !== -1 ? dnisFields[editingDnisIndex]?.phoneNumber : null}
              initialGreetingOverride={editingDnisIndex !== -1 ? dnisFields[editingDnisIndex]?.greetingOverride : null}
              initialAiGreetingOverride={
                editingDnisIndex !== -1 ? dnisFields[editingDnisIndex]?.aiGreetingOverride : null
              }
              isActive={isCompanyActive}
              onCancel={() => setEditingDnisIndex(null)}
              onSave={handleSaveDnis}
            />
          )}
        </Stack>
      </FlexCard>
      {callCenter.phoneCallWebhookEnabled && (
        <FlexCard>
          <PrimaryCardHeader title='Webhooks' />
          <Stack useFlexGap sx={{ padding: 2, width: '100%' }}>
            <CheckboxFormItem
              name='callCenter.phoneCallWebhookEnabled'
              label='Phone call webhook enabled'
              control={control}
              disabled={!!company && !company.isActive}
            />
          </Stack>
        </FlexCard>
      )}
      <FlexCard>
        <PrimaryCardHeader title='Reporting' />
        <Stack useFlexGap sx={{ padding: 2, width: '100%' }} spacing={2}>
          <CheckboxFormItem
            name='callCenter.reporting.shouldReport'
            label='Company reporting enabled'
            control={control}
            disabled={!isCompanyActive}
          />
          <FlexBox sx={{ gap: 2, width: '100%', flexDirection: { xs: 'column', md: 'row' } }}>
            <SelectFormItem
              name='callCenter.reporting.companyCategory'
              control={control}
              label='Company Category'
              fullWidth
              disabled={!isCompanyActive}>
              {allCompanyCategories().map((option) => (
                <MenuItem key={option} value={option}>
                  {companyCategoryDisplayName(option)}
                </MenuItem>
              ))}
            </SelectFormItem>
            <SelectFormItem
              control={control}
              name='callCenter.reporting.invoicedService'
              label='Invoiced Service'
              fullWidth
              disabled={!isCompanyActive}>
              {allInvoicedServices().map((option) => (
                <MenuItem key={option} value={option}>
                  {InvoicedServiceEnum[option]}
                </MenuItem>
              ))}
            </SelectFormItem>
          </FlexBox>
        </Stack>
      </FlexCard>
      {confirmationDialog.element}
    </Stack>
  );
};
